import { useEffect, useMemo, useState } from "react";
import REITab from "../../../components/ui/tab";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import InputSlider from "../../../components/ui/slider";
import REIToggleButton from "../../../components/ui/toggleButton";
import { ITab } from "../../../interface/tab";
import { useCashFlowContext } from "./MyCashFlowContext";
import { ICashFlow } from "../../../interface/cashFlow";
import { httpPost } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY, MyTrackerTabs, USER_DETAIL } from "../../../utils/constants";
import MyCashFlowSummary from "./Summary";
import TrackerPurchaseCost from "./PurchaseCost";
import CashFlowExpense from "./CashFlowExpense";
import Rent from "./Rent/Rent";
import PortfolioTrackerHeader from "../header";
import { Filters } from "../Filters";
import CashFlowSummaryGraph from "./CashFlowSummaryGraph";
import TaxAndDepreciation from "./TaxAndDepreciation";
import Loan from "./Loan/Loan";
import { REIProgressSpinner } from "../../../components/ui";
import { useFilterSelection } from "../FilterSelectionContext";
import { getCurrentFinancialYear } from "../../../utils/commonUtil";
// import CommonConfirmationDialog from "./CommonConfirmationDialog";

const MyCashFlowIndex: React.FC = () => {
  const [isForecastOnly, setIsForecastOnly] = useState<boolean>(false);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const { setCashFlow } = useCashFlowContext();
  const [currentYear, setCurrentYear] = useState<number>(Number(getCurrentFinancialYear()));
  const [dataExist, setDataExist] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [cashFlowData, setCashFlowData] = useState<ICashFlow | undefined>(undefined);
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberId = localStorage.getItem(MEMBER_KEY) || "";
  const { selectedProfiles, selectedEntityId, isShowingTotals, selectedYear, setSelectedYear } = useFilterSelection();

  useEffect(() => {
    setLoading(true);
    const fetchCashFlowData = async () => {
      try {
        const ProfileData =
          selectedProfiles.length > 0
            ? selectedProfiles.map((profile) => ({
                ProfileId: profile.MemberProfileId,
              }))
            : [];
        const Entity = selectedEntityId != 0 ? [{ EntityId: selectedEntityId }] : [];

        const parameter = {
          MemberId: memberId,
          UserCountryCode: memberDetail.Country,
          Entity: Entity,
          Profiles: ProfileData,
          MemberName: `${memberDetail.FirstName} ${memberDetail.LastName}`,
          isFromMyTracker: true,
        };
        const result = await httpPost(`${ApiEndPoints.tracker}/PortfolioTracker/Cashflow/`, parameter);
        if (result.status === 200 && result.data) {
          const data = result.data as ICashFlow;

          if (data.PropertyDetails && data.PropertyDetails[0]?.Details?.CashflowSummaryPortions) {
            const lowestYearRow = findLowestYearRow(data.PropertyDetails);
            setCurrentYear(lowestYearRow ? Number((lowestYearRow as any).Year.slice(2, 4)) : currentYear);
          }

          setCashFlowData(data);
        }
      } catch {
        console.error("Error fetching cash flow data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCashFlowData();
  }, [selectedEntityId, selectedProfiles]);

  const findLowestYearRow = (data: any[]) => {
    let lowestYearRow: { Year: any } | null = null;
    data.forEach((item) => {
      const actuals = item.Details?.CashflowSummaryPortions?.Actuals || [];
      actuals.forEach((actual: any) => {
        if (!lowestYearRow || Number(actual.Year) < Number(lowestYearRow.Year)) {
          lowestYearRow = actual;
        }
      });
    });

    return lowestYearRow;
  };

  useEffect(() => {
    if (cashFlowData) {
      if (
        cashFlowData.PropertyDetails &&
        cashFlowData.PropertyDetails.length > 0 &&
        cashFlowData.TotalForGraph &&
        cashFlowData.TotalForGraph.length > 0
      ) {
        setDataExist(true);
      } else {
        setDataExist(false);
      }
      setCashFlow(cashFlowData);
    }
  }, [cashFlowData]);

  const handleCashFlowUpdate = (updatedProperty: Partial<ICashFlow>) => {
    setCashFlow((prevState) => {
      if (!prevState) return undefined;

      return {
        ...prevState,
        ...updatedProperty,
      };
    });
  };

  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        const ProfileData = selectedProfiles.length > 0 ? selectedProfiles.map((id) => ({ ProfileId: id })) : [];
        const Entity = selectedEntityId != 0 ? [{ EntityId: selectedEntityId }] : [];

        const parameter = {
          MemberId: memberId,
          UserCountryCode: memberDetail.Country,
          Entity: Entity,
          Profiles: ProfileData,
          MemberName: `${memberDetail.FirstName} ${memberDetail.LastName}`,
          isFromMyTracker: true,
        };
        const result = await httpPost(`${ApiEndPoints.tracker}/PortfolioTracker/Cashflow/`, parameter);
        if (result.status === 200 && result.data) {
          const data = result.data as ICashFlow;
          setCashFlowData(data);
        }
      } catch {
        console.error("Error fetching cash flow data.");
      } finally {
        setLoading(false);
      }
    };
    fetchCashFlowData();
  }, [isEditable]);

  const tabs: ITab[] = [
    {
      header: "Summary",
      content: dataExist ? (
        <MyCashFlowSummary
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          year={"20" + selectedYear.toString()}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
    {
      header: "Rent",
      content: dataExist ? (
        <Rent
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          year={"20" + selectedYear.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
    {
      header: "Purchase costs",
      content: dataExist ? (
        <TrackerPurchaseCost
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          year={"20" + selectedYear.toString()}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
    {
      header: "Loan",
      content: dataExist ? (
        <Loan
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          year={"20" + selectedYear.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
    {
      header: "Expenses",
      content: dataExist ? (
        <CashFlowExpense
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          year={"20" + selectedYear.toString()}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
    {
      header: "Tax & Depreciation",
      content: dataExist ? (
        <TaxAndDepreciation
          cashFlowData={cashFlowData}
          isForecastOnly={isForecastOnly}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          year={"20" + selectedYear.toString()}
        />
      ) : (
        <div>
          <span>No data available</span>
        </div>
      ),
      closable: false,
    },
  ];

  const HeaderTemplate = (
    <div className="rounded-t-lg p-2 px-4 bg-slate-600 border-solid text-white border-gray-100 border-x border-y flex gap-3 justify-around">
      <div className="flex w-1/3 content-start">
        <h3>Cash flow in detail</h3>
      </div>
      <div className="flex flex-row gap-3 align-middle w-1/3 content-center">
        <h3>Showing: </h3>
        <InputSlider
          id="mcfidx-sldr-year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.value)}
          min={currentYear}
          max={currentYear + (currentYear < Number(getCurrentFinancialYear()) ? 10 : 9)}
          step={1}
          classNames="self-center w-1/2"
        />
        <b>
          <h3>
            FY-{selectedYear}
            <span className="text-sm">
              {selectedYear.toString() == getCurrentFinancialYear() ? "(Actuals YTD)" : ""}
            </span>
          </h3>
        </b>
      </div>
      <div className="w-1/3 content-center text-end gap-2">
        <REIToggleButton
          id="mcfidx-tgl-forecast"
          customclass="flex-end mr-2"
          index=""
          isSelected={isForecastOnly}
          offlabel="Show only forecasts"
          onToggleChange={() => setIsForecastOnly(!isForecastOnly)}
          onlabel="Show actuals & forecasts"
        />
        <REIToggleButton
          id="mcfidx-tgl-editable"
          index=""
          isSelected={isEditable}
          offlabel="Use wizards to update actuals"
          onToggleChange={() => {
            const currentTab = activeTabIndex;
            setIsEditable(!isEditable);
            if (currentTab === 0) {
              setActiveTabIndex(4);
            } else {
              setActiveTabIndex(currentTab);
            }
          }}
          onlabel="Currently using wizard to edit actuals"
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row flex-wrap gap-4">
      {loading && <REIProgressSpinner></REIProgressSpinner>}
      {!loading && (
        <>
          <PortfolioTrackerHeader
            tabIndex={MyTrackerTabs.MyCashFlow}
            tabName="My Cash Flow"
            hasProperties={dataExist}
            hasPropertiesWithDifferentCurrency={
              cashFlowData?.PropertyDetails?.find((x) => x.PropertyDetail.CurrencyCode != null) ? true : false
            }
            children={<Filters tab="MyCashFlow" />}
          ></PortfolioTrackerHeader>
          <div className="w-full">
            {!dataExist && (
              <REIToggablePanel
                contentComponent={
                  <div className="flex flex-col justify-around">
                    <div className=" mb-4">
                      Start adding your investment properties to Portfolio Tracker and start measuring your cash flow
                      forecasts against its actuals.
                    </div>
                  </div>
                }
                header="Cash Flow Summary"
                istoggleable={false}
                defaultCollapse={false}
              ></REIToggablePanel>
            )}
            {dataExist && (
              <CashFlowSummaryGraph
                isShowingTotals={isShowingTotals}
                selectedProfileId={selectedProfiles.map((p) => p.MemberProfileId)}
                selectedEntityId={selectedEntityId}
                isDataLoading={loading}
                year={Number("20" + selectedYear.toString())}
                onYearChange={(year) => setSelectedYear(year)}
                cashFlowData={cashFlowData}
              ></CashFlowSummaryGraph>
            )}
          </div>
          <div className="w-full">
            <REIToggablePanel
              contentComponent={
                <div className="mx-auto p-4 content-center justified-between-tabview">
                  <REITab
                    id="mcfidx-tab-cashflow"
                    tabs={tabs}
                    classNames="text-center"
                    activeIndex={activeTabIndex}
                    onTabChange={(e) => setActiveTabIndex(e.index)}
                  ></REITab>
                </div>
              }
              defaultCollapse={false}
              header=""
              istoggleable={false}
              customHeader={HeaderTemplate}
            ></REIToggablePanel>
          </div>
        </>
      )}
    </div>
  );
};

export default MyCashFlowIndex;
